/**
 * Horizontal 3-up promo strip. Renders on Landing (unauthed) and Dashboard
 * (authed). Feeds from `GET /api/promotions?limit=3` — but the backend
 * ignores unknown query params, so we just fetch all and slice(0,3) here.
 *
 * The strip is intentionally quiet: 3 compact cards, no accordion, no
 * marketing copy. If the feed is empty (fresh install, no bonuses live),
 * the strip renders nothing so the games grid stays flush against the hero.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ChevronRight, Ticket } from "lucide-react";

import api from "../lib/api";
import { Badge } from "./ui-r1/Badge";
import Button from "./ui-r1/Button";
import { BONUS_TYPE_LABEL, BONUS_TYPE_TONE, currencyChipLabels } from "../lib/bonusHelpers";

export default function BonusPromoStrip({ limit = 3, unauthenticated = false, testid = "promo-strip" }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/promotions")
      .then(({ data }) => alive && setItems((data?.items || []).slice(0, limit)))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, [limit]);

  const layout = useMemo(() => {
    if (items.length === 0) return null;
    return items;
  }, [items]);

  if (!loaded || !layout) return null;

  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Promotions</div>
          <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Boost your play</h2>
        </div>
        <Link
          to={unauthenticated ? "/signup" : "/promotions"}
          className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink"
          data-testid={`${testid}-see-all`}
        >
          See all <ChevronRight className="w-3 h-3 inline" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {layout.map((b) => (
          <MiniBonusCard key={b.slug} bonus={b} unauthenticated={unauthenticated} testid={`${testid}-card-${b.slug}`} />
        ))}
      </div>
    </section>
  );
}

function MiniBonusCard({ bonus, unauthenticated, testid }) {
  const Icon = bonus.bonus_type === "game_free_credit" ? Ticket : Gift;
  const cta =
    bonus.bonus_type === "game_free_credit"
      ? { label: "Claim", to: unauthenticated ? "/signup" : "/promotions" }
      : bonus.bonus_type === "deposit_match"
      ? { label: "Deposit", to: unauthenticated ? "/signup" : `/wallet/deposit${bonus.currencies?.[0] ? `?currency=${bonus.currencies[0]}` : ""}` }
      : { label: "Details", to: unauthenticated ? "/signup" : "/promotions" };
  const chips = currencyChipLabels(bonus.currencies);
  return (
    <div
      className="rounded-xl border border-line bg-elevated hover:border-pink/50 transition p-4 flex flex-col gap-3"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="w-9 h-9 rounded-lg bg-highest border border-line flex items-center justify-center text-pink">
          <Icon className="w-4 h-4" />
        </span>
        <Badge variant={BONUS_TYPE_TONE[bonus.bonus_type] || "muted"}>
          {BONUS_TYPE_LABEL[bonus.bonus_type] || bonus.bonus_type}
        </Badge>
      </div>
      <div>
        <div className="font-display italic text-lg text-ink leading-tight" data-testid={`${testid}-name`}>
          {bonus.name}
        </div>
        <div className="text-[11px] text-ink-2 leading-relaxed mt-1 line-clamp-2">
          {bonus.description}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mt-auto pt-2">
        <div className="flex gap-1 flex-wrap min-w-0">
          {chips.slice(0, 3).map((c) => (
            <span key={c} className="text-[9px] tracking-widest uppercase text-ink-3 border border-line rounded px-1.5 py-0.5">
              {c}
            </span>
          ))}
        </div>
        <Link to={cta.to} data-testid={`${testid}-cta`}>
          <Button variant="pink" size="sm">{cta.label}</Button>
        </Link>
      </div>
    </div>
  );
}
